import { useState, useEffect } from 'react'
import { useAdminUsers } from '@/hooks/useAdmin'

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    admin: 'bg-alvinegro-black text-white',
    moderator: 'bg-alvinegro-dark text-white',
    user: 'bg-alvinegro-light text-alvinegro-dark',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${colors[role] || colors.user}`}>
      {role === 'admin' ? 'Admin' : role === 'moderator' ? 'Moderador' : 'Usuário'}
    </span>
  )
}

export default function UserManager() {
  const { users, loading, totalCount, fetchUsers, updateUserRole, toggleBanUser } = useAdminUsers()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [editUser, setEditUser] = useState<string | null>(null)
  const [newRole, setNewRole] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const perPage = 10

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    fetchUsers(page, perPage, search, roleFilter)
  }, [page, search, roleFilter])

  const handleRoleChange = async (userId: string) => {
    if (!newRole) return
    setActionLoading(true)
    const success = await updateUserRole(userId, newRole)
    if (success) {
      showToast('Papel atualizado com sucesso!')
      setEditUser(null)
    } else {
      showToast('Erro ao atualizar papel', 'error')
    }
    setActionLoading(false)
    fetchUsers(page, perPage, search, roleFilter)
  }

  const handleBan = async (userId: string, banned: boolean) => {
    setActionLoading(true)
    const success = await toggleBanUser(userId, banned)
    if (success) {
      showToast(banned ? 'Usuário banido' : 'Usuário desbanido')
    } else {
      showToast('Erro ao alterar status', 'error')
    }
    setActionLoading(false)
    fetchUsers(page, perPage, search, roleFilter)
  }

  const totalPages = Math.ceil(totalCount / perPage)

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-alvinegro-muted"></i>
            <input
              type="text"
              placeholder="Buscar usuários..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-9 pr-4 py-2 rounded-full border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 rounded-full border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black"
          >
            <option value="">Todos os papéis</option>
            <option value="admin">Admin</option>
            <option value="moderator">Moderador</option>
            <option value="user">Usuário</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-alvinegro-border overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-alvinegro-light" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-alvinegro-light rounded w-1/4" />
                  <div className="h-3 bg-alvinegro-light rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-alvinegro-border bg-alvinegro-light">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Usuário</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden md:table-cell">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Papel</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Cadastro</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-alvinegro-muted">
                        <i className="ri-user-line text-4xl mb-3 block"></i>
                        <p className="text-sm">Nenhum usuário encontrado</p>
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="border-b border-alvinegro-border hover:bg-alvinegro-light/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-alvinegro-black flex items-center justify-center overflow-hidden shrink-0">
                              {user.avatar ? (
                                <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-white font-bold text-sm">{user.name?.substring(0, 2).toUpperCase() || 'U'}</span>
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-alvinegro-black">{user.name || 'Sem nome'}</p>
                              <p className="text-xs text-alvinegro-muted">{user.id.substring(0, 8)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs text-alvinegro-muted">{user.email}</span>
                        </td>
                        <td className="px-4 py-3">
                          {editUser === user.id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                                className="px-2 py-1 rounded-lg border border-alvinegro-border text-xs bg-white"
                              >
                                <option value="">Selecionar</option>
                                <option value="user">Usuário</option>
                                <option value="moderator">Moderador</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button
                                onClick={() => handleRoleChange(user.id)}
                                disabled={!newRole || actionLoading}
                                className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs cursor-pointer"
                              >
                                <i className="ri-check-line"></i>
                              </button>
                              <button
                                onClick={() => setEditUser(null)}
                                className="w-6 h-6 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs cursor-pointer"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          ) : (
                            <RoleBadge role={user.role} />
                          )}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          {user.banned ? (
                            <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-xs border border-red-200">Banido</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-600 text-xs border border-green-200">Ativo</span>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs text-alvinegro-muted">{new Date(user.created_at).toLocaleDateString('pt-BR')}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => { setEditUser(user.id); setNewRole(user.role) }}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-dark transition-colors cursor-pointer"
                              title="Editar papel"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            {user.banned ? (
                              <button
                                onClick={() => handleBan(user.id, false)}
                                disabled={actionLoading}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-50 text-green-600 transition-colors cursor-pointer"
                                title="Desbanir"
                              >
                                <i className="ri-user-follow-line text-sm"></i>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleBan(user.id, true)}
                                disabled={actionLoading}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                                title="Banir"
                              >
                                <i className="ri-user-unfollow-line text-sm"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-alvinegro-border">
                <p className="text-xs text-alvinegro-muted">
                  Mostrando {Math.min((page - 1) * perPage + 1, totalCount)}-{Math.min(page * perPage, totalCount)} de {totalCount}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + Math.max(1, Math.min(page - 2, totalPages - 4))
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 rounded-full text-sm font-medium transition-colors cursor-pointer ${pageNum === page ? 'bg-alvinegro-black text-white' : 'hover:bg-alvinegro-light text-alvinegro-dark'}`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-600'}`}>
            <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}